// The percentage each level of a rubric criterion is worth when its levels
// carry no authored `score` (calculateRubricMark's legacy path).
//
// Port of feedbacksBackend RubricLevelPercentages (used by MarkResolver). The
// shared LTI golden fixtures pin the two together: change both, or neither.
//
// Strongest reading first:
//   1. "0" and non-levels ("Not Shown", "N/A", "U", blank) are worth 0.
//   2. Point bands ("9-10", "17–20", or plain numbers with a "0" level) are
//      worth their midpoint over the top band.
//   3. Published codes (NCEA "N A M E", "HD D CR P N", KS2 "WTS EXS GDS", …)
//      are ranked by what they stand for and placed by position.
//   4. Otherwise the levels are ranked best to worst (see ladderDirection) and
//      known grades keep their band's midpoint (A 95, B 82, High 82, Basic 52);
//      every other level is placed by linear interpolation between them, the
//      ends of the scale counting as 100 and 0 half a level beyond it. With no
//      known grades that is exactly the plain position midpoint.
import {
  isQualifiedPhrase,
  javaTrim,
  meaningScores,
  plainWords,
  positionalOrdering,
  qualifiesMoreIsWorseNoun,
  stableLowerCase,
} from './rubricLadder';

// Grade names → percentage band [min, max]. Iteration order matters: the first
// grade word found inside a longer name wins (mirrors the Java LinkedHashMap).
const GRADE_PERCENTAGES = {
  // Top band names (90-100%)
  a: [90, 100],
  'a+': [95, 100],
  excellent: [90, 100],
  outstanding: [90, 100],
  exemplary: [90, 100],
  distinguished: [90, 100],
  exceptional: [90, 100],
  // High band names (75-89%)
  b: [75, 89],
  'b+': [80, 89],
  good: [75, 89],
  proficient: [75, 89],
  accomplished: [75, 89],
  high: [75, 89],
  // Mid band names (60-74%)
  c: [60, 74],
  'c+': [65, 74],
  satisfactory: [60, 74],
  developing: [60, 74],
  sound: [60, 74],
  medium: [60, 74],
  middle: [60, 74],
  // Lower band names (45-59%)
  d: [45, 59],
  'd+': [50, 59],
  limited: [45, 59],
  beginning: [45, 59],
  basic: [45, 59],
  low: [45, 59],
  // Bottom band names (0-44%)
  e: [0, 44],
  f: [0, 30],
  'not demonstrated': [0, 44],
  inadequate: [0, 44],
  emerging: [20, 44],
  minimal: [0, 30],
};

const GRADE_KEYS = Object.keys(GRADE_PERCENTAGES);
const LETTER_GRADE = /^[a-f]\+?$/;
// Whitespace is spelled out as ASCII: JS and Java disagree on what \s matches.
// "A (17-20)", "B - Above Standard", "C 9-12", "D: …" — a letter grade leading
// the name. "A basic response" (a word follows) is not one.
const LEADING_LETTER_GRADE =
  /^([a-f]\+?)(?:[ \t\n\v\f\r]*[^a-z0-9 \t\n\v\f\r]|[ \t\n\v\f\r]+\d)/;
// "Grade A", "A grade", "17-20 (A Grade)".
const GRADE_NAMED = / grade ([a-f]\+?) | ([a-f]\+?) grade /;
const POINT_BAND =
  /^[ \t\n\v\f\r]*(\d+)[ \t\n\v\f\r]*(?:[-–—][ \t\n\v\f\r]*(\d+))?[ \t\n\v\f\r]*$/;
// Blank: nothing but spaces, dots, dashes, underscores or bullets. Spelled out
// rather than "no letter or digit" because what counts as a letter depends on
// each runtime's Unicode version ("😀", "优秀" and "+++" are real levels).
const BLANK = /^[ \t\n\v\f\r.\-–—_·•]*$/;

// Levels that are not levels: worth 0 and left out when the real ones are
// ranked. Matched on the name's plain words (accents stripped): these short
// codes only as the whole name, the phrases also at its start ("N/A - absent",
// "Not assessed this term", "Absent (medical)").
const NON_LEVEL_CODES = ['na', 'n a', 'ab', 'u', 'ug'];
const NON_LEVEL_PHRASES = [
  'n a',
  'not shown',
  'not applicable',
  'not assessed',
  'not yet assessed',
  'not attempted',
  'no attempt',
  'did not attempt',
  'did not submit',
  'not submitted',
  'no submission',
  'no work submitted',
  'absent',
  'no response',
  'not observed',
  'insufficient evidence',
  'ungraded',
  'unclassified',
  'exempt',
  'excused',
  'not taught',
  'not marked',
  'pending',
  'no evaluado',
  'non note',
  'non evalue',
];

const isNonLevelName = (name) => {
  const words = plainWords(name);
  return (
    NON_LEVEL_CODES.includes(words) ||
    NON_LEVEL_PHRASES.some(
      (phrase) => words === phrase || words.startsWith(`${phrase} `)
    )
  );
};

// Published abbreviation sets, used only when EVERY level of a criterion is a
// code from one set and at least one of its distinctive codes appears. Values
// say what each code stands for (0-100), for ranking; levels are then placed
// by position. Order matters: the first matching set wins.
const CODE_SYSTEMS = [
  // NCEA: Excellence, Merit, Achieved, Not Achieved
  { codes: { e: 95, m: 70, a: 50, n: 5 }, markers: ['m'] },
  // Ontario learning skills (Excellent, Good, Satisfactory, Needs
  // improvement); VCE S / N
  { codes: { e: 95, g: 75, s: 55, n: 15 }, markers: ['g', 's'] },
  // NSW: Consistently, Usually, Sometimes, Rarely, Never
  { codes: { c: 90, u: 70, s: 40, r: 20, n: 5 }, markers: ['u', 'r'] },
  // University: High Distinction, Distinction, Credit, Pass, Fail
  {
    codes: { hd: 95, d: 80, cr: 70, c: 70, p: 55, f: 15, n: 15 },
    markers: ['hd', 'cr', 'p'],
  },
  // BTEC: Distinction*, Distinction, Merit, Pass, Near pass, Unclassified
  {
    codes: { 'd*': 95, d: 85, m: 70, p: 55, n: 35 },
    markers: ['d*', 'm'],
    nonLevels: ['u'],
  },
  // KS1 / KS2 teacher assessment
  {
    codes: { gds: 95, exs: 70, wts: 40, hnm: 30, pkg: 25, pke: 18, pkf: 10 },
    markers: ['gds', 'exs', 'wts', 'hnm', 'pkg', 'pke', 'pkf'],
  },
  // Outstanding, High, Grade Level, Developing, Below Grade Level
  {
    codes: { o: 95, h: 80, gl: 60, d: 40, bgl: 20 },
    markers: ['gl', 'bgl'],
  },
  // QCAA Prep-Year 2: Applying, Making connections, Working with, Exploring,
  // Becoming aware
  {
    codes: { ap: 95, mc: 75, ww: 55, ex: 35, ba: 15 },
    markers: ['mc', 'ww', 'ba'],
  },
  // TASC awards: Exceptional, High, Commendable, Satisfactory, Preliminary,
  // Limited achievement
  {
    codes: { ea: 95, ha: 80, ca: 65, sa: 50, pa: 35, la: 20 },
    markers: ['ea', 'ha', 'ca', 'pa', 'la'],
  },
  // VET: Competent, Not Yet Competent
  { codes: { c: 90, nyc: 20 }, markers: ['nyc'] },
  // Kenya CBC: Exceeding, Meeting, Approaching, Below expectation (1 above 2)
  {
    codes: {
      ee: 95,
      ee1: 97,
      ee2: 93,
      me: 70,
      me1: 72,
      me2: 68,
      ae: 40,
      ae1: 42,
      ae2: 38,
      be: 15,
      be1: 17,
      be2: 13,
    },
    markers: [
      'ee',
      'ee1',
      'ee2',
      'me',
      'me1',
      'me2',
      'ae',
      'ae1',
      'ae2',
      'be',
      'be1',
      'be2',
    ],
  },
  // Philippines DepEd: Always, Sometimes, Rarely, Not Observed
  { codes: { ao: 95, so: 60, ro: 30, no: 5 }, markers: ['ao', 'so', 'ro'] },
  // Ontario elementary report: Levels 4-1, R (remedial), I (insufficient)
  {
    codes: { 4: 90, 3: 75, 2: 65, 1: 55, r: 25, i: 0 },
    markers: ['r'],
  },
  // CEFR: A1 lowest, C2 highest
  {
    codes: { a1: 10, a2: 25, b1: 45, b2: 60, c1: 80, c2: 95 },
    markers: ['b1', 'b2', 'c1', 'c2'],
  },
  // Korean 수우미양가 and Japanese 秀優良可不可
  {
    codes: { 수: 95, 우: 80, 미: 65, 양: 50, 가: 30 },
    markers: ['수', '우', '미', '양', '가'],
  },
  {
    codes: { 秀: 95, 優: 80, 良: 65, 可: 50, 不可: 10 },
    markers: ['秀', '優', '良', '可', '不可'],
  },
  // Scottish Curriculum for Excellence levels: Early .. Fourth
  {
    codes: { early: 10, first: 30, second: 50, third: 70, fourth: 90 },
    markers: ['early', 'fourth'],
  },
  // Queensland: Very High, High, Sound, Limited, Very Limited Achievement
  {
    codes: { vha: 95, ha: 80, sa: 60, la: 35, vla: 15 },
    markers: ['vha', 'vla'],
  },
  // US elementary: Outstanding, Excellent, Very good, Good, Satisfactory,
  // Needs improvement, Unsatisfactory
  {
    codes: { o: 97, e: 95, vg: 85, g: 80, s: 60, ni: 25, n: 25, u: 10 },
    markers: ['s', 'n', 'ni', 'u', 'vg'],
  },
  // Chinese 优秀 / 良好 / 中等 / 及格 / 不及格
  {
    codes: {
      优秀: 95,
      优: 95,
      良好: 75,
      良: 75,
      中等: 60,
      中: 60,
      合格: 55,
      及格: 50,
      差: 15,
      不合格: 10,
      不及格: 10,
    },
    markers: [
      '优秀',
      '优',
      '良好',
      '中等',
      '合格',
      '及格',
      '差',
      '不合格',
      '不及格',
    ],
  },
  // Russian отлично / хорошо / удовлетворительно / неудовлетворительно
  {
    codes: {
      отлично: 95,
      хорошо: 75,
      удовлетворительно: 50,
      неудовлетворительно: 10,
    },
    markers: ['отлично', 'хорошо', 'удовлетворительно', 'неудовлетворительно'],
  },
  // Japanese primary stamps
  {
    codes: {
      たいへんよくできました: 97,
      よくできました: 90,
      できました: 70,
      もう少し: 35,
      もうすこし: 35,
      がんばりましょう: 20,
    },
    markers: [
      'たいへんよくできました',
      'よくできました',
      'できました',
      'もう少し',
      'もうすこし',
      'がんばりましょう',
    ],
  },
  // Faces, thumbs, ticks and traffic-light dots
  {
    codes: {
      '🤩': 97,
      '😀': 95,
      '😃': 95,
      '😄': 95,
      '😁': 95,
      '🌟': 95,
      '✅': 90,
      '✔': 90,
      '😊': 85,
      '👍': 85,
      '🟢': 85,
      '🙂': 75,
      '👌': 70,
      '😐': 50,
      '🟡': 50,
      '😕': 35,
      '🟠': 35,
      '🙁': 25,
      '☹': 20,
      '😟': 20,
      '😞': 15,
      '👎': 15,
      '🔴': 15,
      '😢': 10,
      '❌': 10,
      '✖': 10,
      '😭': 5,
    },
    markers: [
      '🤩',
      '😀',
      '😃',
      '😄',
      '😁',
      '🌟',
      '✅',
      '✔',
      '😊',
      '👍',
      '🟢',
      '🙂',
      '👌',
      '😐',
      '🟡',
      '😕',
      '🟠',
      '🙁',
      '☹',
      '😟',
      '😞',
      '👎',
      '🔴',
      '😢',
      '❌',
      '✖',
      '😭',
    ],
  },
];

// Pasted non-breaking (and other Unicode) spaces and the zero-width BOM read as
// plain spaces, and trimming follows Java's rule, so JS and Java read every
// name the same way.
const UNICODE_SPACES = /[   -   　﻿]/g;

// The emoji variation selector (U+FE0F) is dropped so "☹️" and "☹" match.
const normalise = (name) =>
  stableLowerCase(
    javaTrim(
      String(name ?? '')
        .replace(UNICODE_SPACES, ' ')
        .replace(/\uFE0F/g, '')
    )
  );

// Words only, single-spaced; '+' kept so "a+" stays one token.
const wordsOf = (normalised) => normalised.replace(/[^a-z0-9+]+/g, ' ').trim();

const bandMidpoint = (gradeKey) => {
  const [min, max] = GRADE_PERCENTAGES[gradeKey];
  return (min + max) / 2;
};

const isBlank = (normalised) => BLANK.test(normalised);

// A code system reads codes by pattern too: PSLE "AL1".."AL8" (1 best), old
// National Curriculum "4a".."4c" (a top), and letter-number grades whose
// letters vary ("A1 | B2 | … | F9": the letter decides, lower number better).
// A level made of one symbol repeated ("⭐⭐⭐", "+++"), the same symbol in
// every level: more is better.
const repeatedSymbolCount = (level) => {
  const symbols = Array.from(level);
  return symbols.length > 0 &&
    !/[a-z0-9]/.test(symbols[0]) &&
    symbols.every((symbol) => symbol === symbols[0])
    ? symbols.length
    : null;
};

const patternMeaning = (levels) => {
  const firstSymbols = new Set(levels.map((level) => Array.from(level)[0]));
  if (
    firstSymbols.size === 1 &&
    levels.every((level) => repeatedSymbolCount(level) !== null)
  ) {
    return (level) => repeatedSymbolCount(level);
  }
  // SQA bands run 1 (best) to 7; NSW HSC bands run 1 to 6 with 6 best, so only
  // a set reaching Band 7 and no further reads the SQA way.
  const bands = levels.map((level) => wordsOf(level).match(/^band ([1-9])$/));
  if (
    bands.every(Boolean) &&
    bands.some((band) => band[1] === '7') &&
    bands.every((band) => Number(band[1]) <= 7)
  ) {
    return (level) => 100 - 12 * Number(wordsOf(level).slice(5));
  }
  if (levels.every((level) => /^al[1-8]$/.test(level))) {
    return (level) => 100 - 10 * Number(level.slice(2));
  }
  // JLPT N1 (best) .. N5; Irish Leaving Cert H1 / O1 (best) .. H8 / O8.
  if (levels.every((level) => /^n[1-5]$/.test(level))) {
    return (level) => 100 - 15 * Number(level.slice(1));
  }
  if (levels.every((level) => /^[ho][1-8]$/.test(level))) {
    return (level) =>
      (level[0] === 'h' ? 100 : 60) - 5 * Number(level.slice(1));
  }
  if (levels.every((level) => /^[1-8][abc]$/.test(level))) {
    const sublevel = { a: 6, b: 4, c: 2 };
    return (level) => 10 * Number(level[0]) + sublevel[level[1]];
  }
  const letterNumber = /^([a-f])([0-9]?)$/;
  const letters = new Set(levels.map((level) => level[0]));
  if (
    levels.every((level) => letterNumber.test(level)) &&
    letters.size >= 2 &&
    levels.some((level) => level.length === 2)
  ) {
    return (level) =>
      bandMidpoint(level[0]) - (level.length === 2 ? Number(level[1]) : 0);
  }
  return null;
};

// The code system every level belongs to, if any: { meaning, nonLevels }.
const codeSystemOf = (normalisedLevels) => {
  const levels = [
    ...new Set(normalisedLevels.filter((level) => !isBlank(level))),
  ].filter((level) => wordsOf(level) !== '0');
  if (levels.length === 0) {
    return null;
  }
  const system = CODE_SYSTEMS.find(
    ({ codes, markers, nonLevels = [] }) =>
      levels.every(
        (level) =>
          Object.prototype.hasOwnProperty.call(codes, level) ||
          nonLevels.includes(level)
      ) && levels.some((level) => markers.includes(level))
  );
  if (system) {
    return {
      meaning: (level) => system.codes[level],
      nonLevels: system.nonLevels || [],
    };
  }
  const byPattern = patternMeaning(levels);
  return byPattern ? { meaning: byPattern, nonLevels: [] } : null;
};

const isZeroLevel = (normalised, codeSystem) => {
  if (isBlank(normalised) || wordsOf(normalised) === '0') {
    return true;
  }
  return codeSystem
    ? codeSystem.nonLevels.includes(normalised)
    : isNonLevelName(normalised);
};

// The grade a name stands for, and how sure that reading is: the whole name
// ('exact', or 'letter' for A-F), a letter grade leading it ('letter'), or a
// grade word inside it ('word': "Very high" → high). A lone letter inside a
// longer name is never a grade: as a substring it read "Basic" and "Band 5" as
// A. A grade word qualifying a more-is-worse noun ("Limited support") is not
// a grade either.
const gradeOf = (name, normalised) => {
  if (Object.prototype.hasOwnProperty.call(GRADE_PERCENTAGES, normalised)) {
    return {
      key: normalised,
      kind: LETTER_GRADE.test(normalised) ? 'letter' : 'exact',
    };
  }
  const leading = normalised.match(LEADING_LETTER_GRADE);
  if (leading) {
    return { key: leading[1], kind: 'letter' };
  }
  const padded = ` ${wordsOf(normalised)} `;
  const named = padded.match(GRADE_NAMED);
  if (named) {
    return { key: named[1] || named[2], kind: 'letter' };
  }
  const key = GRADE_KEYS.find(
    (grade) =>
      grade.length > 1 &&
      !grade.endsWith('+') &&
      padded.includes(` ${grade} `) &&
      !qualifiesMoreIsWorseNoun(name, grade) &&
      !isQualifiedPhrase(name, grade)
  );
  return key ? { key, kind: 'word' } : null;
};

// What the levels mean on a 0-100 scale, for ordering only: their quality
// wording, else their grade band, else unknown (null).
const meaningsOf = (names, grades) =>
  meaningScores(names).map((meaning, k) => {
    if (meaning !== null) {
      return meaning;
    }
    return grades[k] ? bandMidpoint(grades[k].key) : null;
  });

const pointBandOf = (normalised) => {
  const match = normalised.match(POINT_BAND);
  if (!match) {
    return null;
  }
  const first = Number(match[1]);
  const second = match[2] === undefined ? first : Number(match[2]);
  return [Math.min(first, second), Math.max(first, second)];
};

const strictlyMonotonic = (values) => {
  let rising = true;
  let falling = true;
  for (let i = 1; i < values.length; i += 1) {
    rising = rising && values[i] > values[i - 1];
    falling = falling && values[i] < values[i - 1];
  }
  return rising || falling;
};

// Weakly monotonic one way with at least one strict step: true = rising
// (worst-first), false = falling (best-first), null = neither.
const exactDirection = (values) => {
  let up = false;
  let down = false;
  for (let i = 1; i < values.length; i += 1) {
    up = up || values[i] > values[i - 1];
    down = down || values[i] < values[i - 1];
  }
  if (up === down) {
    return null;
  }
  return up;
};

// Direction from per-level meanings in list order: true = worst-first, false =
// best-first, null = unclear. Needs a real spread (authors order the low-end
// words basic / limited / minimal every which way) and at least three quarters
// of the weighted pairwise evidence pointing one way.
export const meaningDirection = (meanings) => {
  if (meanings.length < 2) {
    return null;
  }
  if (Math.max(...meanings) - Math.min(...meanings) < 25) {
    return null;
  }
  let rising = 0;
  let falling = 0;
  for (let i = 0; i < meanings.length; i += 1) {
    for (let j = i + 1; j < meanings.length; j += 1) {
      const step = meanings[j] - meanings[i];
      if (step > 0) {
        rising += step;
      } else {
        falling -= step;
      }
    }
  }
  if (rising >= 3 * falling) {
    return true;
  }
  return falling >= 3 * rising ? false : null;
};

// Direction from how well the unmistakable meanings (85+ or 15-) fit each
// end: a meaning near where its level would sit read best-first, against read
// worst-first. Needs a gap of 25 points ("unsafe | … | exemplary conduct": 95 at the bottom of a
// best-first reading is far off, at the top of a worst-first one it fits).
export const fitDirection = (meanings) => {
  const total = meanings.length;
  let bestFirstMiss = 0;
  let worstFirstMiss = 0;
  meanings.forEach((meaning, position) => {
    // Only unmistakable wording counts: one mid-scale word ("minor", "sound")
    // must not overturn the usual best-first reading.
    if (meaning === null || (meaning > 15 && meaning < 85)) {
      return;
    }
    bestFirstMiss += Math.abs(meaning - positionPercentage(position, total));
    worstFirstMiss += Math.abs(
      meaning - positionPercentage(total - 1 - position, total)
    );
  });
  if (bestFirstMiss - worstFirstMiss >= 25) {
    return true;
  }
  return worstFirstMiss - bestFirstMiss >= 25 ? false : null;
};

const ladderDirection = (names, grades) => {
  const meanings = meaningsOf(names, grades);
  const byMeaning = meaningDirection(
    meanings.filter((meaning) => meaning !== null)
  );
  if (byMeaning !== null) {
    return byMeaning;
  }
  const byNumbers = positionalOrdering(names);
  if (byNumbers !== null) {
    return byNumbers;
  }
  return fitDirection(meanings) === true;
};

// Point bands: every level a number or range, at least one range or a "0"
// level (bare "8 | 9 | 10 | 11" are year levels, not points), midpoints
// strictly ordered, and a finite top band above zero.
const pointPercentages = (normalisedLevels, hasZeroLevel) => {
  const bands = normalisedLevels.map(pointBandOf);
  if (bands.some((band) => band === null)) {
    return null;
  }
  const hasRange = bands.some(([low, high]) => low !== high);
  const top = Math.max(...bands.map(([, high]) => high));
  const midpoints = bands.map(([low, high]) => (low + high) / 2);
  const ordered = midpoints.length === 1 || strictlyMonotonic(midpoints);
  if (
    !(hasRange || hasZeroLevel) ||
    !(top > 0) ||
    !Number.isFinite(top) ||
    !midpoints.every((midpoint) => Number.isFinite(midpoint)) ||
    !ordered
  ) {
    return null;
  }
  return midpoints.map((midpoint) => (midpoint / top) * 100);
};

// Grades kept as fixed points, by rank (0 = best): whole-name and letter grades
// must fall strictly down the ranks or none are trusted (HD | D | C | P | F
// uses D for Distinction); a grade word inside a longer name is kept only where
// it fits strictly between its kept neighbours ("Very high" next to "High" does
// not, so it is interpolated above it).
const fixedPoints = (gradesByRank) => {
  const fixed = new Map();
  let previous = Infinity;
  for (let rank = 0; rank < gradesByRank.length; rank += 1) {
    const grade = gradesByRank[rank];
    if (grade && grade.kind !== 'word') {
      const value = bandMidpoint(grade.key);
      if (!(value < previous)) {
        return new Map();
      }
      fixed.set(rank, value);
      previous = value;
    }
  }
  gradesByRank.forEach((grade, rank) => {
    if (!grade || grade.kind !== 'word') {
      return;
    }
    const value = bandMidpoint(grade.key);
    const [, above] = neighbourAbove(fixed, rank);
    const [, below] = neighbourBelow(fixed, rank);
    if (
      (above === null || value < above) &&
      (below === null || value > below)
    ) {
      fixed.set(rank, value);
    }
  });
  return fixed;
};

const neighbourAbove = (fixed, rank) => {
  let best = [null, null];
  fixed.forEach((value, fixedRank) => {
    if (fixedRank < rank && (best[0] === null || fixedRank > best[0])) {
      best = [fixedRank, value];
    }
  });
  return best;
};

const neighbourBelow = (fixed, rank) => {
  let best = [null, null];
  fixed.forEach((value, fixedRank) => {
    if (fixedRank > rank && (best[0] === null || fixedRank < best[0])) {
      best = [fixedRank, value];
    }
  });
  return best;
};

const positionPercentage = (rank, total) => {
  const step = 100 / total;
  const maxPercent = 100 - rank * step;
  const minPercent = maxPercent - step;
  return (minPercent + maxPercent) / 2;
};

const rankedPercentage = (fixed, rank, total) => {
  if (fixed.size === 0) {
    return positionPercentage(rank, total);
  }
  if (fixed.has(rank)) {
    return fixed.get(rank);
  }
  const [aboveRank, aboveValue] = neighbourAbove(fixed, rank);
  const [belowRank, belowValue] = neighbourBelow(fixed, rank);
  const fromRank = aboveRank === null ? -0.5 : aboveRank;
  const fromValue = aboveRank === null ? 100 : aboveValue;
  const toRank = belowRank === null ? total - 0.5 : belowRank;
  const toValue = belowRank === null ? 0 : belowValue;
  return (
    fromValue +
    ((toValue - fromValue) * (rank - fromRank)) / (toRank - fromRank)
  );
};

// Positions 0..n-1 in the order the levels should be ranked, best first when
// meanings decided it: by code meaning for a code system, by meaning for a
// mistyped criterion whose levels all have one; else null (keep list order).
const meaningOrder = (meanings) =>
  meanings.some((meaning) => meaning === null || meaning === undefined)
    ? null
    : meanings
        .map((_, k) => k)
        .sort(
          (left, right) => meanings[right] - meanings[left] || left - right
        );

// Percentages for the distinct real levels, in their list order.
const distinctLevelPercentages = (
  names,
  normalisedLevels,
  mistyped,
  hasZeroLevel,
  hasNonLevel,
  codeSystem
) => {
  const points = pointPercentages(normalisedLevels, hasZeroLevel);
  if (points) {
    return points;
  }
  // One real level beside non-levels ("Submitted | Not submitted") is the top
  // of two.
  if (names.length === 1 && hasNonLevel) {
    return [75];
  }
  const grades = codeSystem
    ? normalisedLevels.map(() => null)
    : normalisedLevels.map((level, k) => gradeOf(names[k], level));
  let order = names.map((_, k) => k);
  let worstFirst;
  if (codeSystem) {
    const meanings = normalisedLevels.map(codeSystem.meaning);
    const reordered = mistyped ? meaningOrder(meanings) : null;
    order = reordered || order;
    worstFirst = reordered ? false : exactDirection(meanings) === true;
  } else {
    const reordered = mistyped ? meaningOrder(meaningsOf(names, grades)) : null;
    order = reordered || order;
    worstFirst = reordered
      ? false
      : ladderDirection(
          order.map((k) => names[k]),
          order.map((k) => grades[k])
        );
  }
  const total = order.length;
  const rankOf = (position) => (worstFirst ? total - 1 - position : position);
  const gradesByRank = new Array(total);
  order.forEach((k, position) => {
    gradesByRank[rankOf(position)] = grades[k];
  });
  const fixed = fixedPoints(gradesByRank);
  const percentages = new Array(total);
  order.forEach((k, position) => {
    percentages[k] = rankedPercentage(fixed, rankOf(position), total);
  });
  return percentages;
};

// One percentage per level, in the order given.
export const levelPercentages = (levelNames = []) => {
  const names = levelNames.map((name) => String(name ?? ''));
  const normalised = names.map(normalise);
  const codeSystem = codeSystemOf(normalised);
  const firstIndexOf = new Map();
  const distinct = [];
  let counted = 0;
  normalised.forEach((level, index) => {
    if (isZeroLevel(level, codeSystem)) {
      return;
    }
    counted += 1;
    if (!firstIndexOf.has(level)) {
      firstIndexOf.set(level, distinct.length);
      distinct.push(index);
    }
  });
  const percentages =
    distinct.length === 0
      ? []
      : distinctLevelPercentages(
          distinct.map((index) => names[index]),
          distinct.map((index) => normalised[index]),
          distinct.length < counted,
          normalised.some((level) => wordsOf(level) === '0'),
          counted < normalised.length,
          codeSystem
        );
  return normalised.map((level) =>
    isZeroLevel(level, codeSystem) ? 0 : percentages[firstIndexOf.get(level)]
  );
};
